import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import { getContractorAndArchitect, updateLiveTask } from '../services/api/implementation/impl';
// The same user list the task edit form uses.
import { getUsers } from '../../super-admin/services/api/implementation/impl';

/**
 * Assign or change who works on one task, from the task's own details panel:
 * site team (engineer / supervisor — Operations users), contractors and
 * architects. Asked for on prod, 16 Oct; until now this needed the full edit
 * form or Bulk Actions.
 *
 * Saves through PUT /liveTask/:id with only the lists that changed. The
 * backend replaces exactly those mappings and leaves everything else on the
 * task alone; its rule for the site team stands: one Manager, at most one
 * Executive.
 */
const SECTIONS = [
  { key: 'team', label: 'Engineer / Supervisor' },
  { key: 'contractors', label: 'Contractors' },
  { key: 'architects', label: 'Architects' }
];

const levelLabel = (level) => (level === 'Medium' ? 'Manager' : level === 'Low' ? 'Executive' : null);

const AssignTaskPeopleModal = ({ task, initialSection = 'team', onClose, onSaved }) => {
  const [section, setSection] = useState(initialSection);
  const [search, setSearch] = useState('');

  const startTeam = useMemo(
    () => (task?.engineerLiveTaskMapping ?? []).map((m) => m?.user?.id).filter(Boolean),
    [task]
  );
  const startContractors = useMemo(
    () =>
      (task?.spLiveTaskMapping ?? [])
        .filter((m) => m?.user?.serviceProvider?.category_id === 1)
        .map((m) => m?.user?.id)
        .filter(Boolean),
    [task]
  );
  const startArchitects = useMemo(
    () =>
      (task?.architectLiveTaskMapping ?? [])
        .filter((m) => m?.user?.serviceProvider?.category_id === 2)
        .map((m) => m?.user?.id)
        .filter(Boolean),
    [task]
  );

  const [team, setTeam] = useState(startTeam);
  const [contractors, setContractors] = useState(startContractors);
  const [architects, setArchitects] = useState(startArchitects);

  const users = useQuery({ queryKey: ['users'], queryFn: () => getUsers() });
  const contractorList = useQuery({ queryKey: ['getContractorAndArchitect1'], queryFn: () => getContractorAndArchitect(1) });
  const architectList = useQuery({ queryKey: ['getContractorAndArchitect2'], queryFn: () => getContractorAndArchitect(2) });

  const teamOptions = useMemo(() => {
    const rows = users.data?.data ?? users.data ?? [];
    return (Array.isArray(rows) ? rows : [])
      .filter((u) => u.vertical === 'Operations' && (u.level === 'Medium' || u.level === 'Low'))
      .map((u) => ({
        id: u.id,
        name: u.name,
        level: u.level,
        hint: [levelLabel(u.level), u.designation?.name].filter(Boolean).join(' · ')
      }));
  }, [users.data]);
  const contractorOptions = useMemo(
    () =>
      (contractorList.data?.data ?? []).map((sp) => ({
        id: sp.user?.id,
        name: sp.user?.name,
        hint: (sp.serviceProviderWorkTypeMapping ?? []).map((m) => m?.workType?.name).filter(Boolean).join(', ')
      })).filter((o) => o.id),
    [contractorList.data]
  );
  const architectOptions = useMemo(
    () => (architectList.data?.data ?? []).map((sp) => ({ id: sp.user?.id, name: sp.user?.name, hint: '' })).filter((o) => o.id),
    [architectList.data]
  );

  const current = {
    team: { options: teamOptions, value: team, set: setTeam, loading: users.isLoading },
    contractors: { options: contractorOptions, value: contractors, set: setContractors, loading: contractorList.isLoading },
    architects: { options: architectOptions, value: architects, set: setArchitects, loading: architectList.isLoading }
  }[section];

  const shown = current.options.filter((o) =>
    search ? String(o.name ?? '').toLowerCase().includes(search.toLowerCase()) : true
  );

  const toggle = (id) => {
    current.set((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
  const managers = teamOptions.filter((o) => team.includes(o.id) && o.level === 'Medium').length;
  const executives = teamOptions.filter((o) => team.includes(o.id) && o.level === 'Low').length;
  const teamProblem =
    team.length > 0 && managers !== 1
      ? 'Pick exactly one Manager for the site team.'
      : executives > 1
        ? 'Pick at most one Executive (supervisor).'
        : null;

  const save = useMutation({
    mutationFn: () => {
      const payload = {};
      if (!sameSet(team, startTeam)) payload.engineer_ids = team;
      if (!sameSet(contractors, startContractors)) payload.sp_ids = contractors;
      if (!sameSet(architects, startArchitects)) payload.architect_ids = architects;
      if (!Object.keys(payload).length) return Promise.resolve(null);
      return updateLiveTask(task.id, payload);
    },
    onSuccess: (result) => {
      if (result !== null) toast.success('Assignments saved');
      onSaved?.();
      onClose();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save the assignments')
  });

  const counts = { team: team.length, contractors: contractors.length, architects: architects.length };

  return (
    <Modal name={`Assign people — ${task?.name ?? 'task'}`} onClose={onClose} width="560px">
      <div className="space-y-3">
        <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-0.5" role="tablist">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={s.key === section}
              onClick={() => {
                setSection(s.key);
                setSearch('');
              }}
              className={`flex-1 rounded-md px-2 py-1.5 text-sm font-medium ${
                s.key === section ? 'bg-[#2A565E] text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              {s.label} ({counts[s.key]})
            </button>
          ))}
        </div>

        {section === 'team' && (
          <p className="text-xs text-gray-500">One Manager is required; add at most one Executive (supervisor).</p>
        )}

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />

        <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
          {current.loading ? (
            <p className="p-4 text-center text-sm text-gray-500">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="p-4 text-center text-sm text-gray-500">Nobody to show.</p>
          ) : (
            shown.map((o) => (
              <label
                key={o.id}
                className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={current.value.includes(o.id)}
                  onChange={() => toggle(o.id)}
                />
                <span className="flex-1">
                  <span className="font-medium text-gray-900">{o.name}</span>
                  {o.hint ? <span className="block text-xs text-gray-500">{o.hint}</span> : null}
                </span>
              </label>
            ))
          )}
        </div>

        {teamProblem && <p className="text-xs text-red-600">{teamProblem}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button displayType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || Boolean(teamProblem)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AssignTaskPeopleModal;
